import { useMemo, useState } from 'react';
import {
    Button,
    Divider,
    Empty,
    Grid,
    Input,
    InputNumber,
    Select,
    Space,
    Table,
    Tag,
    Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
    MATERIAL_LABELS,
    MATERIAL_OPTIONS,
    TABLE_51_MATERIALS,
    formatGrams,
} from '../../bdo/types';
import type { MaterialKey, PackagingElement } from '../../bdo/types';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export interface EditablePackagingItem {
    key: string;
    id?: string;
    elementId: string;
    material: MaterialKey;
    massGrams: number;
}

interface Props {
    items: EditablePackagingItem[];
    elements: PackagingElement[];
    onChange: (items: EditablePackagingItem[]) => void;
    onCreateElement: (name: string) => Promise<PackagingElement | undefined>;
    onManageElements?: () => void;
    isMobile?: boolean;
    disabled?: boolean;
}

export function newPackagingItem(): EditablePackagingItem {
    return {
        key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        elementId: '',
        material: 'PLASTIC',
        massGrams: 0,
    };
}

export default function PackagingItemsTable({
    items,
    elements,
    onChange,
    onCreateElement,
    onManageElements,
    isMobile,
    disabled,
}: Props) {
    const screens = useBreakpoint();
    const mobile = isMobile ?? screens.md === false;
    const [newElementName, setNewElementName] = useState<Record<string, string>>({});
    const [, setAddingFor] = useState<string | null>(null);

    const totalGrams = useMemo(
        () => items.reduce((sum, item) => sum + (Number(item.massGrams) || 0), 0),
        [items],
    );

    const update = (key: string, patch: Partial<EditablePackagingItem>) => {
        onChange(items.map((item) => (item.key === key ? { ...item, ...patch } : item)));
    };

    const handleCreateElement = async (rowKey: string) => {
        const name = (newElementName[rowKey] || '').trim();
        if (!name) return;
        const element = await onCreateElement(name);
        if (element) {
            update(rowKey, { elementId: element.id });
            setNewElementName((prev) => ({ ...prev, [rowKey]: '' }));
            setAddingFor(null);
        }
    };

    const getShare = (massGrams: number) =>
        totalGrams > 0 ? ((Number(massGrams) || 0) / totalGrams) * 100 : 0;

    const activeElements = elements.filter((e) => e.isActive);

    const renderElementSelect = (record: EditablePackagingItem) => (
        <Select
            value={record.elementId || undefined}
            placeholder="Wybierz element"
            showSearch
            optionFilterProp="label"
            disabled={disabled}
            style={{ width: '100%' }}
            options={activeElements.map((el) => ({ value: el.id, label: el.name }))}
            onChange={(value: string) => update(record.key, { elementId: value })}
            dropdownRender={(menu) => (
                <>
                    {menu}
                    <Divider style={{ margin: '6px 0' }} />
                    <Space.Compact style={{ width: '100%', padding: '0 4px 4px' }}>
                        <Input
                            size="small"
                            placeholder="Nowy element"
                            value={newElementName[record.key] || ''}
                            onChange={(e) =>
                                setNewElementName((prev) => ({
                                    ...prev,
                                    [record.key]: e.target.value,
                                }))
                            }
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleCreateElement(record.key);
                                }
                            }}
                        />
                        <Button
                            size="small"
                            type="primary"
                            onClick={() => handleCreateElement(record.key)}
                        >
                            Dodaj
                        </Button>
                    </Space.Compact>
                </>
            )}
        />
    );

    const renderMaterialSelect = (record: EditablePackagingItem) => (
        <Select
            value={record.material}
            disabled={disabled}
            style={{ width: '100%' }}
            options={MATERIAL_OPTIONS}
            onChange={(next: MaterialKey) => update(record.key, { material: next })}
        />
    );

    const renderMassInput = (record: EditablePackagingItem) => (
        <InputNumber
            value={record.massGrams}
            min={0}
            precision={2}
            disabled={disabled}
            style={{ width: '100%' }}
            addonAfter="g"
            onChange={(next) => update(record.key, { massGrams: Number(next) || 0 })}
        />
    );

    const columns = [
        {
            title: 'Element opakowania',
            dataIndex: 'elementId',
            key: 'elementId',
            width: 260,
            render: (_: string, record: EditablePackagingItem) =>
                renderElementSelect(record),
        },
        {
            title: 'Materiał',
            dataIndex: 'material',
            key: 'material',
            width: 210,
            render: (_: MaterialKey, record: EditablePackagingItem) =>
                renderMaterialSelect(record),
        },
        {
            title: 'Masa [g/szt.]',
            dataIndex: 'massGrams',
            key: 'massGrams',
            width: 140,
            align: 'right' as const,
            render: (_: number, record: EditablePackagingItem) => renderMassInput(record),
        },
        {
            title: 'Udział %',
            key: 'share',
            width: 90,
            align: 'right' as const,
            render: (_: any, record: EditablePackagingItem) => (
                <Text type="secondary">{getShare(record.massGrams).toFixed(1)}%</Text>
            ),
        },
        {
            title: '',
            key: 'actions',
            width: 50,
            render: (_: any, record: EditablePackagingItem) =>
                !disabled ? (
                    <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => onChange(items.filter((i) => i.key !== record.key))}
                    />
                ) : null,
        },
    ];

    const byMaterial = items.reduce((acc: Record<string, number>, item) => {
        acc[item.material] = (acc[item.material] || 0) + (Number(item.massGrams) || 0);
        return acc;
    }, {} as Record<string, number>);

    const mass51 = TABLE_51_MATERIALS.reduce(
        (sum: number, material: MaterialKey) => sum + (byMaterial[material] || 0),
        0,
    );
    const mass52 = byMaterial.MULTIMATERIAL || 0;
    const massOther = byMaterial.OTHER || 0;

    const renderMobileItems = () => {
        if (!items.length) {
            return (
                <div style={{ padding: '24px 8px' }}>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Brak składników — dodaj pierwszy element."
                    />
                </div>
            );
        }

        return (
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
                {items.map((record, index) => (
                    <div
                        key={record.key}
                        style={{
                            border: '1px solid #f0f0f0',
                            borderRadius: 10,
                            padding: 12,
                            background: '#fafafa',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 8,
                                marginBottom: 10,
                            }}
                        >
                            <Text strong>Element {index + 1}</Text>
                            {!disabled && (
                                <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() =>
                                        onChange(items.filter((i) => i.key !== record.key))
                                    }
                                />
                            )}
                        </div>

                        <div style={{ marginBottom: 10 }}>
                            <Text
                                type="secondary"
                                style={{ display: 'block', fontSize: 12, marginBottom: 4 }}
                            >
                                Element opakowania
                            </Text>
                            {renderElementSelect(record)}
                        </div>

                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(auto-fit, minmax(140px, 1fr))',
                                gap: 10,
                            }}
                        >
                            <div>
                                <Text
                                    type="secondary"
                                    style={{
                                        display: 'block',
                                        fontSize: 12,
                                        marginBottom: 4,
                                    }}
                                >
                                    Materiał
                                </Text>
                                {renderMaterialSelect(record)}
                            </div>
                            <div>
                                <Text
                                    type="secondary"
                                    style={{
                                        display: 'block',
                                        fontSize: 12,
                                        marginBottom: 4,
                                    }}
                                >
                                    Masa [g/szt.]
                                </Text>
                                {renderMassInput(record)}
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginTop: 10,
                            }}
                        >
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Udział w masie
                            </Text>
                            <Tag color="blue" style={{ margin: 0 }}>
                                {getShare(record.massGrams).toFixed(1)}%
                            </Tag>
                        </div>
                    </div>
                ))}
            </Space>
        );
    };

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="small">
            <div
                style={{
                    display: 'flex',
                    flexDirection: mobile ? 'column' : 'row',
                    alignItems: mobile ? 'stretch' : 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                }}
            >
                <Text strong>Opakowania BDO</Text>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: mobile ? 'column' : 'row',
                        flexWrap: 'wrap',
                        gap: 8,
                        width: mobile ? '100%' : undefined,
                    }}
                >
                    {onManageElements && (
                        <Button
                            size="small"
                            onClick={onManageElements}
                            disabled={disabled}
                            style={{ width: mobile ? '100%' : undefined }}
                        >
                            Słownik elementów
                        </Button>
                    )}
                    <Button
                        size="small"
                        type="dashed"
                        icon={<PlusOutlined />}
                        disabled={disabled}
                        onClick={() => onChange([...items, newPackagingItem()])}
                        style={{ width: mobile ? '100%' : undefined }}
                    >
                        Dodaj element
                    </Button>
                </div>
            </div>

            <Space wrap size={4}>
                <Text strong>Razem: {formatGrams(totalGrams)} g/szt.</Text>
                {Object.entries(byMaterial)
                    .filter(([, grams]) => grams > 0)
                    .map(([material, grams]) => (
                        <Tag key={material} style={{ margin: 0 }}>
                            {MATERIAL_LABELS[material as MaterialKey] || material}:{' '}
                            {formatGrams(grams)} g
                        </Tag>
                    ))}
            </Space>

            {mobile ? (
                renderMobileItems()
            ) : (
                <Table
                    dataSource={items}
                    columns={columns}
                    rowKey="key"
                    size="small"
                    pagination={false}
                    scroll={{ x: 750 }}
                    locale={{ emptyText: 'Brak składników — dodaj pierwszy element.' }}
                />
            )}

            <Space direction="vertical" size={2}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Do tabeli 5.1: {formatGrams(mass51)} g · Pozostałe (tylko 1A):{' '}
                    {formatGrams(massOther)} g
                </Text>
                {mass52 > 0 && (
                    <Text type="warning" style={{ fontSize: 12 }}>
                        Wielomateriałowe (5.2, poza 5.1): {formatGrams(mass52)} g
                    </Text>
                )}
            </Space>
        </Space>
    );
}
